import { useMemo } from 'react';

export interface PolicyModalProps {
  title: string;
  subtitle?: string;
  lastUpdated?: string;
  content: readonly string[];
  onClose: () => void;
}

const numberingRegex = /^(\d+(?:\.\d+)*)([.)]?\s*)(.+)$/;

function normalizeNumberedParagraphs(paragraphs: readonly string[]): string[] {
  const counters: number[] = [];

  return paragraphs.map((paragraph) => {
    const match = paragraph.match(numberingRegex);
    if (!match) {
      return paragraph;
    }

    const [, , , rest] = match;
    const level = match[1].split('.').length;

    while (counters.length < level) counters.push(0);
    counters.length = level;
    counters[level - 1] += 1;

    const newNumber = counters.join('.');
    return `${newNumber}. ${rest.trim()}`;
  });
}

export function PolicyModal({ title, subtitle, lastUpdated, content, onClose }: PolicyModalProps) {
  const normalizedContent = useMemo(() => normalizeNumberedParagraphs(content), [content]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10">
      <button
        aria-label="Close policy modal"
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B0F17] via-[#050505] to-[#0B0F17] shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
        <div className="absolute -top-24 -left-16 w-64 h-64 bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-10 w-72 h-72 bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative overflow-y-auto max-h-[85vh] custom-scrollbar">
          <div className="p-8 md:p-10 space-y-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                {lastUpdated && (
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">
                    Last updated: {lastUpdated}
                  </p>
                )}
                <h1 className="text-3xl md:text-4xl font-bold text-white mt-2">{title}</h1>
                {subtitle && <p className="text-slate-400 mt-2 text-sm md:text-base">{subtitle}</p>}
              </div>

              <button
                onClick={onClose}
                className="self-start px-3 py-2 rounded-xl border border-white/10 text-slate-300 hover:text-white hover:border-emerald-400/50 transition-all bg-white/5"
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6l12 12M6 18L18 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Close
                </span>
              </button>
            </div>

            <div className="space-y-3 text-sm md:text-base text-slate-200 leading-relaxed">
              {normalizedContent.map((paragraph, index) => (
                <p key={`${index}-${paragraph.slice(0, 12)}`} className="text-slate-300">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PolicyModal;
